import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";

function fmtDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("cs-CZ");
}

function fmtDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("cs-CZ");
}

function badgeClass(status) {
  switch (status) {
    case "completed":
      return "bg-green-100 text-green-800 border border-green-200";
    case "overdue":
      return "bg-red-100 text-red-800 border border-red-200";
    case "expired":
      return "bg-orange-100 text-orange-800 border border-orange-200";
    case "waived":
      return "bg-slate-100 text-slate-700 border border-slate-200";
    default:
      return "bg-amber-100 text-amber-800 border border-amber-200";
  }
}

function badgeLabel(status) {
  switch (status) {
    case "completed":
      return "Splněno";
    case "overdue":
      return "Po termínu";
    case "expired":
      return "Propadlé";
    case "waived":
      return "Výjimka";
    default:
      return "Čeká";
  }
}

export default function TrainingRecordsPage() {
  const { user } = useAuth();

  const [records, setRecords] = useState([]);
  const [definitions, setDefinitions] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  const [statusFilter, setStatusFilter] = useState("");
  const [trainingFilter, setTrainingFilter] = useState("");
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [detail, setDetail] = useState(null);

  const [completeModal, setCompleteModal] = useState(null);
  const [completeForm, setCompleteForm] = useState({
    completedAt: "",
    notes: "",
  });

  const firma = user?.firma || "";
  const role = user?.role || "";

  const canManageAdvanced = role === "admin" || role === "rm";

  const pageTitle =
    role === "user" ? "Školení zaměstnanců pobočky" : "Přehled plnění školení";

  async function fetchDefinitions() {
    const res = await fetch(`/api/training/${encodeURIComponent(firma)}/definitions?active=true`, {
      credentials: "include",
    });
    const data = await res.json().catch(() => []);
    if (!res.ok) throw new Error(data?.message || "Nepodařilo se načíst školení.");
    setDefinitions(Array.isArray(data) ? data : []);
  }

  async function fetchEmployees() {
    if (!firma) {
      setEmployees([]);
      return;
    }

    let url = "";

    if (role === "user") {
      const pharmacyCode = String(user?.pharmacyCode || "").trim();

      if (!pharmacyCode) {
        setEmployees([]);
        return;
      }

      url = `/api/employees/${encodeURIComponent(firma)}/${encodeURIComponent(pharmacyCode)}/visible`;
    } else if (role === "rm" || role === "admin") {
      url = `/api/employees/${encodeURIComponent(firma)}`;
    } else {
      setEmployees([]);
      return;
    }

    const res = await fetch(url, { credentials: "include" });
    const data = await res.json().catch(() => []);

    if (!res.ok) {
      throw new Error(data?.message || "Nepodařilo se načíst zaměstnance.");
    }

    setEmployees(Array.isArray(data) ? data : []);
  }

  async function fetchRecords() {
    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);
    if (trainingFilter) params.set("trainingId", trainingFilter);
    if (employeeFilter) params.set("employeeId", employeeFilter);

    const qs = params.toString();
    const res = await fetch(
      `/api/training/${encodeURIComponent(firma)}/records${qs ? `?${qs}` : ""}`,
      { credentials: "include" }
    );
    const data = await res.json().catch(() => []);
    if (!res.ok) throw new Error(data?.message || "Nepodařilo se načíst záznamy.");
    setRecords(Array.isArray(data) ? data : []);
  }

  async function loadAll() {
    if (!firma) return;
    setLoading(true);
    setError("");

    try {
      await Promise.all([fetchDefinitions(), fetchEmployees(), fetchRecords()]);
    } catch (err) {
      setError(err.message || "Chyba při načítání.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firma, role, user?.pharmacyCode]);

  useEffect(() => {
    fetchRecords().catch((err) => setError(err.message || "Chyba při načítání záznamů."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, trainingFilter, employeeFilter]);

  const sortedEmployees = useMemo(() => {
    return [...employees].sort((a, b) => {
      const nameCompare = String(a.fullName || "").localeCompare(
        String(b.fullName || ""),
        "cs"
      );

      if (nameCompare !== 0) return nameCompare;

      return String(a.pharmacyCode || "").localeCompare(
        String(b.pharmacyCode || ""),
        "cs"
      );
    });
  }, [employees]);

  function openCompleteModal(record) {
    setCompleteModal(record);

    const existingDate = record.completedAt
      ? new Date(record.completedAt).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10);

    setCompleteForm({
      completedAt: existingDate,
      notes: record.notes || "",
    });
  }

  async function markComplete() {
    if (!completeModal) return;

    try {
      setBusyId(completeModal._id);

      const res = await fetch(
        `/api/training/${encodeURIComponent(firma)}/records/${completeModal._id}/complete`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            completedAt: completeForm.completedAt
              ? `${completeForm.completedAt}T12:00:00`
              : undefined,
            notes: completeForm.notes || "",
            completionSource: "manual",
          }),
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Nepodařilo se uložit splnění.");

      await fetchRecords();

      if (detail?._id === completeModal._id) {
        await openDetail(completeModal._id);
      }

      setCompleteModal(null);
      setCompleteForm({
        completedAt: "",
        notes: "",
      });
    } catch (err) {
      alert(err.message || "Chyba při ukládání.");
    } finally {
      setBusyId(null);
    }
  }

  async function resetFromModal() {
    if (!completeModal) return;

    if (
      !window.confirm(
        `Opravdu zrušit splnění pro "${completeModal.employeeNameSnapshot}"?`
      )
    ) {
      return;
    }

    try {
      setBusyId(completeModal._id);

      const res = await fetch(
        `/api/training/${encodeURIComponent(firma)}/records/${completeModal._id}/reset`,
        {
          method: "PATCH",
          credentials: "include",
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || "Nepodařilo se zrušit splnění.");
      }

      await fetchRecords();

      if (detail?._id === completeModal._id) {
        await openDetail(completeModal._id);
      }

      setCompleteModal(null);
      setCompleteForm({
        completedAt: "",
        notes: "",
      });
    } catch (err) {
      alert(err.message || "Chyba při rušení splnění.");
    } finally {
      setBusyId(null);
    }
  }  

  async function resetCompletion(record) {
    if (!window.confirm(`Opravdu resetovat splnění pro "${record.employeeNameSnapshot}"?`)) {
      return;
    }

    try {
      setBusyId(record._id);
      const res = await fetch(
        `/api/training/${encodeURIComponent(firma)}/records/${record._id}/reset`,
        {
          method: "PATCH",
          credentials: "include",
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Nepodařilo se resetovat splnění.");

      await fetchRecords();
      if (detail?._id === record._id) {
        await openDetail(record._id);
      }
    } catch (err) {
      alert(err.message || "Chyba při resetu.");
    } finally {
      setBusyId(null);
    }
  }

  async function updateNotes(record) {
    const notes = window.prompt("Upravit poznámku:", record.notes || "");
    if (notes === null) return;

    try {
      setBusyId(record._id);
      const res = await fetch(
        `/api/training/${encodeURIComponent(firma)}/records/${record._id}/notes`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ notes }),
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Nepodařilo se uložit poznámku.");

      await fetchRecords();
      if (detail?._id === record._id) {
        await openDetail(record._id);
      }
    } catch (err) {
      alert(err.message || "Chyba při ukládání poznámky.");
    } finally {
      setBusyId(null);
    }
  }

  async function setWaived(record) {
    const reason = window.prompt("Důvod výjimky:", record.waivedReason || "");
    if (reason === null) return;

    try {
      setBusyId(record._id);
      const res = await fetch(
        `/api/training/${encodeURIComponent(firma)}/records/${record._id}/waive`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reason }),
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Nepodařilo se nastavit výjimku.");

      await fetchRecords();
      if (detail?._id === record._id) {
        await openDetail(record._id);
      }
    } catch (err) {
      alert(err.message || "Chyba při ukládání výjimky.");
    } finally {
      setBusyId(null);
    }
  }

  async function unsetWaived(record) {
    try {
      setBusyId(record._id);
      const res = await fetch(
        `/api/training/${encodeURIComponent(firma)}/records/${record._id}/unwaive`,
        {
          method: "PATCH",
          credentials: "include",
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Nepodařilo se zrušit výjimku.");

      await fetchRecords();
      if (detail?._id === record._id) {
        await openDetail(record._id);
      }
    } catch (err) {
      alert(err.message || "Chyba při rušení výjimky.");
    } finally {
      setBusyId(null);
    }
  }

  async function recomputeStatuses() {
    try {
      setBusyId("__recompute__");
      const res = await fetch(
        `/api/training/${encodeURIComponent(firma)}/recompute-statuses`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Nepodařilo se přepočítat stavy.");

      await fetchRecords();
      alert(`Přepočet dokončen. Změněno záznamů: ${data.changed ?? 0}`);
    } catch (err) {
      alert(err.message || "Chyba při přepočtu.");
    } finally {
      setBusyId(null);
    }
  }

  async function openDetail(recordId) {
    try {
      const res = await fetch(
        `/api/training/${encodeURIComponent(firma)}/records/${recordId}`,
        {
          credentials: "include",
        }
      );
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.message || "Nepodařilo se načíst detail.");
      setDetail(data);
    } catch (err) {
      alert(err.message || "Chyba při načítání detailu.");
    }
  }

  const stats = useMemo(() => {
    return {
      total: records.length,
      pending: records.filter((x) => x.status === "pending").length,
      completed: records.filter((x) => x.status === "completed").length,
      overdue: records.filter((x) => x.status === "overdue").length,
      expired: records.filter((x) => x.status === "expired").length,
      waived: records.filter((x) => x.status === "waived").length,
    };
  }, [records]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{pageTitle}</h1>
          <p className="text-sm text-slate-600 mt-1">
            Evidence splnění, termínů a auditní historie školení.
          </p>
        </div>

        {canManageAdvanced && (
          <button
            type="button"
            onClick={recomputeStatuses}
            disabled={busyId === "__recompute__"}
            className="rounded-xl bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-60"
          >
            Přepočítat stavy
          </button>
        )}
      </div>

      <div className="grid gap-3 md:grid-cols-6">
        <StatCard label="Celkem" value={stats.total} />
        <StatCard label="Čeká" value={stats.pending} />
        <StatCard label="Splněno" value={stats.completed} />
        <StatCard label="Po termínu" value={stats.overdue} />
        <StatCard label="Propadlé" value={stats.expired} />
        <StatCard label="Výjimka" value={stats.waived} />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Stav</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-3 py-2"
            >
              <option value="">Vše</option>
              <option value="pending">Čeká</option>
              <option value="completed">Splněno</option>
              <option value="overdue">Po termínu</option>
              <option value="expired">Propadlé</option>
              <option value="waived">Výjimka</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Školení</label>
            <select
              value={trainingFilter}
              onChange={(e) => setTrainingFilter(e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-3 py-2"
            >
              <option value="">Všechna</option>
              {definitions.map((item) => (
                <option key={item._id} value={item._id}>
                  {item.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Zaměstnanec</label>
            <select
              value={employeeFilter}
              onChange={(e) => setEmployeeFilter(e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-3 py-2"
            >
              <option value="">Všichni</option>
              {sortedEmployees.map((emp) => (
                <option key={emp._id} value={emp._id}>
                  {emp.fullName}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              type="button"
              onClick={() => {
                setStatusFilter("");
                setTrainingFilter("");
                setEmployeeFilter("");
              }}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 hover:bg-slate-50"
            >
              Reset filtrů
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-6 text-slate-600">Načítám…</div>
        ) : error ? (
          <div className="p-6 text-red-600">{error}</div>
        ) : records.length === 0 ? (
          <div className="p-6 text-slate-600">Žádné záznamy školení.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-700">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Zaměstnanec</th>
                  <th className="px-4 py-3 text-left font-semibold">Pozice</th>
                  <th className="px-4 py-3 text-left font-semibold">Školení</th>
                  <th className="px-4 py-3 text-left font-semibold">Stav</th>
                  <th className="px-4 py-3 text-left font-semibold">Termín</th>
                  <th className="px-4 py-3 text-left font-semibold">Splněno</th>
                  <th className="px-4 py-3 text-left font-semibold">Platné do</th>
                  <th className="px-4 py-3 text-left font-semibold">Akce</th>
                </tr>
              </thead>
              <tbody>
                {records.map((record) => (
                  <tr key={record._id} className="border-t border-slate-100 align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">
                        {record.employeeNameSnapshot}
                      </div>
                      <div className="text-xs text-slate-500">
                        Pobočka: {record.pharmacyCode || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{record.positionSnapshot || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{record.trainingTitleSnapshot}</div>
                      <div className="text-xs text-slate-500">{record.trainingCode}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${badgeClass(record.status)}`}>
                        {badgeLabel(record.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{fmtDate(record.dueDate)}</td>
                    <td className="px-4 py-3 text-slate-700">{fmtDate(record.completedAt)}</td>
                    <td className="px-4 py-3 text-slate-700">{fmtDate(record.validUntil)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => openDetail(record._id)}
                          className="rounded-lg border border-slate-300 px-3 py-1.5 hover:bg-slate-50"
                        >
                          Detail
                        </button>

                        {record.status !== "waived" && (
                          <button
                            type="button"
                            onClick={() => openCompleteModal(record)}
                            className="rounded-lg bg-blue-600 px-3 py-1.5 text-white hover:bg-blue-700"
                          >
                            Upravit
                          </button>
                        )}

                        {canManageAdvanced && record.status !== "waived" && (
                          <button
                            type="button"
                            onClick={() => setWaived(record)}
                            disabled={busyId === record._id}
                            className="rounded-lg border border-slate-300 px-3 py-1.5 hover:bg-slate-50 disabled:opacity-60"
                          >
                            Výjimka
                          </button>
                        )}

                        {canManageAdvanced && record.status === "waived" && (
                          <button
                            type="button"
                            onClick={() => unsetWaived(record)}
                            disabled={busyId === record._id}
                            className="rounded-lg border border-slate-300 px-3 py-1.5 hover:bg-slate-50 disabled:opacity-60"
                          >
                            Zrušit výjimku
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detail && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {detail.trainingTitleSnapshot}
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  {detail.employeeNameSnapshot} · {detail.positionSnapshot || "—"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDetail(null)}
                className="rounded-lg border border-slate-300 px-3 py-1.5 hover:bg-slate-50"
              >
                Zavřít
              </button>
            </div>

            <div className="grid gap-6 p-5 lg:grid-cols-2">
              <div className="space-y-3">
                <SectionTitle>Základní údaje</SectionTitle>
                <DetailRow label="Stav" value={badgeLabel(detail.status)} />
                <DetailRow label="Termín" value={fmtDate(detail.dueDate)} />
                <DetailRow label="Splněno" value={fmtDateTime(detail.completedAt)} />
                <DetailRow label="Platné do" value={fmtDate(detail.validUntil)} />
                <DetailRow label="Zdroj splnění" value={detail.completionSource || "—"} />
                <DetailRow label="Poznámka" value={detail.notes || "—"} />
                <DetailRow label="Výjimka – důvod" value={detail.waivedReason || "—"} />
              </div>

              <div className="space-y-3">
                <SectionTitle>Audit</SectionTitle>
                <div className="max-h-[50vh] space-y-3 overflow-y-auto rounded-xl border border-slate-200 p-3">
                  {(detail.audit || []).length === 0 ? (
                    <div className="text-sm text-slate-500">Bez historie.</div>
                  ) : (
                    [...detail.audit]
                      .slice()
                      .reverse()
                      .map((item, idx) => (
                        <div key={`${item.action}-${item.at}-${idx}`} className="rounded-xl bg-slate-50 p-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800">
                              {item.action}
                            </span>
                            <span className="text-xs text-slate-500">
                              {fmtDateTime(item.at)}
                            </span>
                          </div>

                          <div className="mt-1 text-sm text-slate-700">
                            {item.by?.name || "Neznámý uživatel"} {item.by?.role ? `(${item.by.role})` : ""}
                          </div>

                          {!!item.meta && (
                            <pre className="mt-2 overflow-x-auto rounded-lg bg-white p-2 text-xs text-slate-700 border border-slate-200">
{JSON.stringify(item.meta, null, 2)}
                            </pre>
                          )}
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {completeModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {completeModal?.status === "completed" ? "Upravit splnění" : "Označit jako splněné"}
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  {completeModal.employeeNameSnapshot} · {completeModal.trainingTitleSnapshot}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Aktuální stav: {badgeLabel(completeModal.status)}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setCompleteModal(null)}
                className="rounded-lg border border-slate-300 px-3 py-1.5 hover:bg-slate-50"
              >
                Zavřít
              </button>
            </div>

            <div className="space-y-4 p-5">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Datum splnění
                </label>
                <input
                  type="date"
                  value={completeForm.completedAt}
                  onChange={(e) =>
                    setCompleteForm((prev) => ({
                      ...prev,
                      completedAt: e.target.value,
                    }))
                  }
                  className="w-full rounded-xl border border-slate-300 px-3 py-2"
                />
                <div className="mt-1 text-xs text-slate-500">
                  Po uložení se datum zobrazí ve formátu dd.mm.rrrr.
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Poznámka
                </label>
                <textarea
                  value={completeForm.notes}
                  onChange={(e) =>
                    setCompleteForm((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  rows={4}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2"
                  placeholder="Volitelná poznámka ke splnění"
                />
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-3 border-t px-5 py-4">
              {completeModal.status === "completed" && canManageAdvanced && (
                <button
                  type="button"
                  onClick={resetFromModal}
                  disabled={busyId === completeModal._id}
                  className="rounded-xl border border-red-300 px-4 py-2 text-red-700 hover:bg-red-50 disabled:opacity-60"
                >
                  Zrušit splnění
                </button>
              )}

              <button
                type="button"
                onClick={() => setCompleteModal(null)}
                className="rounded-xl border border-slate-300 px-4 py-2 hover:bg-slate-50"
              >
                Zavřít
              </button>

              <button
                type="button"
                onClick={markComplete}
                disabled={busyId === completeModal._id}
                className="rounded-xl bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:opacity-60"
              >
                Uložit změny
              </button>
            </div>
          </div>
        </div>
      )}      
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-sm text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{children}</h3>;
}

function DetailRow({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-sm text-slate-900 whitespace-pre-wrap">{value}</div>
    </div>
  );
}